import React, { useState, useEffect, useRef } from 'react';
import { carteraService, CarteraOperacion, CarteraTrimestre } from '../services/carteraService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { BsWallet2 } from 'react-icons/bs';
import { formatCurrency as fmt } from '../utils/format';
import './CarteraRevalorizacion.css';

const fmtPct = (v: number | null | undefined, decimals = 6): string => {
  if (v === undefined || v === null) return '-';
  return `${Number(v).toFixed(decimals)}%`;
};

const fmtDate = (d: string | null | undefined): string => {
  if (!d) return '-';
  const date = new Date(d);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const fmtCoef = (v: number | null | undefined): string => {
  if (v === undefined || v === null) return '-';
  return Number(v).toFixed(2);
};

const FIXED_COLS = 15;

const CarteraRevalorizacion: React.FC = () => {
  const [operaciones, setOperaciones] = useState<CarteraOperacion[]>([]);
  const [trimestres, setTrimestres] = useState<CarteraTrimestre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await carteraService.getCartera();
      setOperaciones(res.data.operaciones);
      setTrimestres(res.data.trimestres);
    } catch {
      setError('Error al cargar los datos de cartera');
    } finally {
      setLoading(false);
    }
  };

  const totalTickets = operaciones.reduce((sum, op) => sum + (op.ticketsEmitidos ?? 0), 0);
  const totalCoste = operaciones.reduce((sum, op) => sum + (op.costeInversion ?? 0), 0);
  const totalPctActual = operaciones.reduce((sum, op) => sum + (op.porcentajeActual ?? 0), 0);

  return (
    <div className="cartera-page">
      <div className="tabs">
        <button className="tab active">Cartera, revalorización Tickets</button>
      </div>

      <div className="cartera-content">
        {loading && <LoadingSpinner />}
        {error && <div className="cartera-error">{error}</div>}

        {!loading && !error && operaciones.length === 0 && (
          <EmptyState
            icon={BsWallet2}
            title="Sin operaciones"
            subtitle="No hay operaciones de tickets registradas."
          />
        )}

        {!loading && !error && operaciones.length > 0 && (
          <div className="cartera-table-wrapper" ref={tableRef}>
            <table className="cartera-table">
              <thead>
                {/* Section header bar */}
                <tr className="cartera-section-header">
                  <td colSpan={FIXED_COLS} className="cartera-section-title">
                    Cartera y revalorización de tickets — {operaciones.length} operaciones · {totalTickets} tickets
                  </td>
                  {trimestres.map((_, idx) => (
                    <td key={idx}></td>
                  ))}
                </tr>
                <tr>
                  <th className="cartera-col-num" rowSpan={2}>N.º Op.</th>
                  <th className="cartera-col-codigo" rowSpan={2}>Código</th>
                  <th className="cartera-col-activo" rowSpan={2}>Activo</th>
                  <th rowSpan={2}>Coste inversión</th>
                  <th rowSpan={2}>Fecha adquisición</th>
                  <th rowSpan={2}>Tickets emitidos</th>
                  <th rowSpan={2}>Valor inicial ticket</th>
                  <th rowSpan={2}>Fecha emisión</th>
                  <th rowSpan={2}>Fecha reval. contable</th>
                  <th rowSpan={2}>Valor contable antes</th>
                  <th rowSpan={2}>Valor contable después</th>
                  <th rowSpan={2}>% emisión</th>
                  <th rowSpan={2}>% actual</th>
                  <th rowSpan={2}>Coef. reval.</th>
                  <th rowSpan={2}>Coef. reval. acc.</th>
                  {trimestres.length > 0 && (
                    <th colSpan={trimestres.length} className="cartera-th-timeline">
                      % Tickets por trimestre
                    </th>
                  )}
                </tr>
                <tr>
                  {trimestres.map((t, idx) => (
                    <th key={idx} className="cartera-th-quarter">
                      <div className="cartera-quarter-header">
                        {t.numOperacion != null && (
                          <span className="cartera-quarter-op">{t.numOperacion}</span>
                        )}
                        <span className="cartera-quarter-label">{t.label}</span>
                        <span className="cartera-quarter-date">{t.fecha}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {operaciones.map((op, rowIdx) => (
                  <tr key={rowIdx} className="cartera-data-row">
                    <td className="cartera-col-num">{op.numOperacion}</td>
                    <td className="cartera-col-codigo">
                      <span className="cartera-codigo-badge">{op.codigoOperacion || '-'}</span>
                    </td>
                    <td className="cartera-col-activo" title={op.activo || ''}>
                      {op.activo || '-'}
                    </td>
                    <td className="cartera-td-money">{fmt(op.costeInversion)}</td>
                    <td className="cartera-td-date">{fmtDate(op.fechaAdquisicion)}</td>
                    <td className="cartera-td-number">{op.ticketsEmitidos ?? '-'}</td>
                    <td className="cartera-td-money">{fmt(op.valorInicialTicket)}</td>
                    <td className="cartera-td-date">{fmtDate(op.fechaEmision)}</td>
                    <td className="cartera-td-date">{fmtDate(op.fechaRevalorizacionContable)}</td>
                    <td className="cartera-td-money">{fmt(op.valorContableAntes)}</td>
                    <td className="cartera-td-money">{fmt(op.valorContableDespues)}</td>
                    <td className="cartera-td-pct">{fmtPct(op.porcentajeEmision)}</td>
                    <td className="cartera-td-pct">{fmtPct(op.porcentajeActual)}</td>
                    <td className="cartera-td-coef">{fmtCoef(op.coefRevalorizacion)}</td>
                    <td className="cartera-td-coef">{fmtCoef(op.coefRevalorizacionAcc)}</td>
                    {trimestres.map((t, colIdx) => {
                      const val = t.porcentajeTickets[rowIdx];
                      return (
                        <td key={colIdx} className={`cartera-td-timeline ${val != null ? 'cartera-td-active' : ''}`}>
                          {val != null ? fmtPct(val) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Totals row */}
                <tr className="cartera-totals-row">
                  <td className="cartera-col-num"></td>
                  <td className="cartera-col-codigo"><strong>TOTAL</strong></td>
                  <td className="cartera-col-activo"></td>
                  <td className="cartera-td-money">{fmt(totalCoste)}</td>
                  <td className="cartera-td-date"></td>
                  <td className="cartera-td-number">{totalTickets}</td>
                  <td className="cartera-td-money"></td>
                  <td className="cartera-td-date"></td>
                  <td className="cartera-td-date"></td>
                  <td className="cartera-td-money"></td>
                  <td className="cartera-td-money"></td>
                  <td className="cartera-td-pct"></td>
                  <td className="cartera-td-pct">{fmtPct(totalPctActual)}</td>
                  <td className="cartera-td-coef"></td>
                  <td className="cartera-td-coef"></td>
                  {trimestres.map((_, colIdx) => (
                    <td key={colIdx} className="cartera-td-timeline"></td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarteraRevalorizacion;
